/**
 * CSRF Protection Utilities
 * Implements CSRF token management and request protection
 */

// CSRF token storage (in production, this should be set via httpOnly cookie)
let csrfToken = null;

// Generate a random CSRF token
const generateCSRFToken = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join(
    "",
  );
};

// Get current CSRF token or generate new one
export const getCSRFToken = () => {
  if (!csrfToken) {
    csrfToken = generateCSRFToken();
    // In production, this should be set by server via httpOnly cookie
    if (import.meta.env.DEV) {
      document.cookie = `csrf_token=${csrfToken}; path=/; secure=${!import.meta.env.DEV}; samesite=strict`;
    }
  }
  return csrfToken;
};

// Get CSRF token from cookie (server-set)
export const getCSRFTokenFromCookie = () => {
  const nameEQ = "csrf_token=";
  const ca = document.cookie.split(";");

  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) {
      return c.substring(nameEQ.length, c.length);
    }
  }

  return null;
};

// Refresh CSRF token
export const refreshCSRFToken = () => {
  csrfToken = null;
  return getCSRFToken();
};

// Validate state for CSRF protection (for sensitive operations)
export const validateCSRFState = (state) => {
  // This should validate that the state parameter matches expected value
  // Implementation depends on your specific requirements
  return typeof state === "string" && state.length > 0;
};

// Add CSRF token to request headers
export const addCSRFToHeaders = (headers = {}) => {
  const token = getCSRFTokenFromCookie() || getCSRFToken();
  if (token) {
    headers["X-CSRF-Token"] = token;
  }
  return headers;
};

// Check if request should include CSRF protection
export const requiresCSRFProtection = (url, method = "GET") => {
  // Only state-changing requests need CSRF protection
  const stateChangingMethods = ["POST", "PUT", "DELETE", "PATCH"];

  // Skip CSRF for auth endpoints (they have their own protection)
  const skipCSRFPatterns = [
    "/api/auth/login",
    "/api/auth/check",
    "/api/auth/getNewToken",
    "/api/auth/logoff",
  ];

  return (
    stateChangingMethods.includes(method.toUpperCase()) &&
    !skipCSRFPatterns.some((pattern) => url.includes(pattern))
  );
};
